import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Briefcase, ArrowRight } from 'lucide-react';
import { competencyRequestAdminApi } from '@/lib/competencyProfileApi';
import { parseApiDate } from '@/lib/utils';

const ageDays = (iso) => {
  const d = parseApiDate(iso);
  return d ? Math.floor((Date.now() - d.getTime()) / 86400000) : null;
};

// UAT A3 — pending Job Profile creation work must be visible from the Job
// Profile workspaces, not only via notification deep-links.
export const PendingJobProfileRequests = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    competencyRequestAdminApi.list({ status: 'pending', type_: 'new_profile' })
      .then((rows) => setItems(rows.filter((r) => r.type === 'new_profile')))
      .catch(() => setItems([]));
  }, []);

  if (!items.length) return null;
  return (
    <Card className="p-4 border-warning/40 bg-warning/5 shadow-card" data-testid="pending-job-profile-requests">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-warning" />
          {t('tco.pendingJp.title', { defaultValue: 'Solicitudes pendientes de Perfil de Puesto' })}
          <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 tabular-nums">{items.length}</Badge>
        </div>
        <Button
          variant="ghost" size="sm" className="text-xs gap-1"
          onClick={() => navigate('/admin/talentcompetency/requests')}
          data-testid="pending-jp-open-inbox"
        >
          {t('tco.pendingJp.openInbox', { defaultValue: 'Ver todas' })} <ArrowRight className="h-3 w-3" />
        </Button>
      </div>
      <div className="space-y-2">
        {items.slice(0, 5).map((r) => {
          const age = ageDays(r.requested_at);
          return (
            <div key={r.id} className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2" data-testid={`pending-jp-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{r.proposed_title || t('tco.client.untitled')}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {[r.company_name, r.department, r.requested_by_name].filter(Boolean).join(' · ')}
                </p>
              </div>
              {r.assigned_advisor_name && (
                <span className="text-xs text-muted-foreground hidden md:block">{r.assigned_advisor_name}</span>
              )}
              {age !== null && (
                <Badge variant="outline" className={`text-xs tabular-nums ${age >= 3 ? 'text-destructive border-destructive/30' : 'text-muted-foreground'}`}>
                  {t('tco.pendingJp.age', { defaultValue: '{{n}} d', n: age })}
                </Badge>
              )}
              <Button
                size="sm" variant="outline" className="h-7 text-xs shrink-0"
                onClick={() => navigate(`/admin/talentcompetency/requests?rid=${r.id}`)}
                data-testid={`pending-jp-open-${r.id}`}
              >
                {t('tco.pendingJp.open', { defaultValue: 'Abrir solicitud' })}
              </Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
